import type { WeeklyHoursValue } from '@jordan/ui';

/** A small curated timezone list for the schedule/timezone selects. */
export const TIMEZONES = [
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Los_Angeles',
  'America/Toronto',
  'America/Vancouver',
  'Europe/London',
  'Europe/Paris',
  'Europe/Berlin',
  'Asia/Tokyo',
  'Australia/Sydney',
  'UTC',
];

export const LOCATION_LABELS: Record<string, string> = {
  GOOGLE_MEET: 'Google Meet',
  ZOOM: 'Zoom',
  PHONE: 'Phone',
  IN_PERSON: 'In person',
  CUSTOM: 'Custom',
};

export interface ApiRule {
  dayOfWeek: number;
  startMinute: number;
  endMinute: number;
}

export function rulesToWeekly(rules: ApiRule[]): WeeklyHoursValue {
  const out: WeeklyHoursValue = {};
  for (const r of rules) {
    (out[r.dayOfWeek] ??= []).push({ start: r.startMinute, end: r.endMinute });
  }
  return out;
}

export function weeklyToRules(value: WeeklyHoursValue): ApiRule[] {
  const rules: ApiRule[] = [];
  for (const [day, ranges] of Object.entries(value)) {
    for (const r of ranges) {
      if (r.end > r.start) rules.push({ dayOfWeek: Number(day), startMinute: r.start, endMinute: r.end });
    }
  }
  return rules;
}

// The host used to build/display public booking links. Both book.digitalbaxter.com and mymtg.io
// serve the booking app (see isBookingHost); this just picks which one generated links show.
// Override with VITE_BOOKING_HOST (e.g. https://mymtg.io). TODO(config): move to the config service.
export const BOOKING_HOST = (import.meta.env.VITE_BOOKING_HOST ?? 'https://book.digitalbaxter.com').replace(/\/$/, '');

/** Host portion of BOOKING_HOST (no scheme), e.g. "mymtg.io" — for display hints. */
export const BOOKING_HOST_NAME = BOOKING_HOST.replace(/^https?:\/\//, '');

/** True when a hostname is one of the public booking hosts (so the SPA renders the booking app). */
export function isBookingHost(hostname: string): boolean {
  return hostname.startsWith('book.') || hostname === 'mymtg.io' || hostname === 'www.mymtg.io';
}

/** Public booking URL for an event type (served from the booking host). */
export function bookingUrl(slug: string): string {
  return `${BOOKING_HOST}/${slug}`;
}

/** The owner's public landing page (the booking host root). */
export function landingUrl(): string {
  return `${BOOKING_HOST}/`;
}

const DAY_ABBR = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

function formatMinutes(min: number): string {
  const h = Math.floor(min / 60);
  const m = min % 60;
  const period = h < 12 ? 'AM' : 'PM';
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return m === 0 ? `${h12} ${period}` : `${h12}:${String(m).padStart(2, '0')} ${period}`;
}

export interface AvailabilitySummary {
  days: number[];
  sameWindow: { startMinute: number; endMinute: number } | null;
}

/** Render an availability summary like "Mon, Wed, Fri, 9 AM – 5 PM" or "…, hours vary". */
export function formatAvailability(a: AvailabilitySummary | undefined): string {
  if (!a || a.days.length === 0) return 'No availability set';
  const days = a.days.map((d) => DAY_ABBR[d]).join(', ');
  const hours = a.sameWindow
    ? `${formatMinutes(a.sameWindow.startMinute)} – ${formatMinutes(a.sameWindow.endMinute)}`
    : 'hours vary';
  return `${days}, ${hours}`;
}
